import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { FancyRenderingProvider } from '@/components/RichTextEditor/utils/fancyRenderContext';
import { cn } from '@/utils/tailwind';

import { PlaygroundSectionHeader } from '../Playground/PlaygroundHome/PlaygroundSectionHeader';
import { usePlaygroundStore } from '../Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { PlaygroundBody } from '../Playground/components/PlaygroundBody';
import { PLAYGROUND_LAYOUT_SIZES } from '../Playground/components/constants';
import { EvaluatorPlaygroundFeedbackSchemaPanel } from './EvaluatorPlaygroundFeedbackSchemaPanel';
import { EvaluatorPromptOutputSection } from './EvaluatorPromptOutputSection';
import { EvaluatorPromptPanel } from './EvaluatorPromptPanel';

export function EvaluatorPlaygroundLayout({
  scrollRef,
}: {
  scrollRef?: Dispatch<SetStateAction<HTMLDivElement | null>>;
}) {
  const prompts = usePlaygroundStore((state) => state.prompts);

  const outputSection = useMemo(() => {
    return (
      <div className="flex h-full flex-col overflow-x-auto">
        <FancyRenderingProvider>
          <EvaluatorPromptOutputSection />
        </FancyRenderingProvider>
      </div>
    );
  }, []);

  const [promptCollapseState, setPromptCollapseState] = useState<
    Record<number, boolean>
  >(Object.fromEntries(prompts.map((_, index) => [index, false])));

  const toggleCollapseAllMessagesForPrompt = useCallback(
    (index: number, isAllCollapsed?: boolean) => {
      setPromptCollapseState((prev) => ({
        ...prev,
        [index]: isAllCollapsed ?? !prev[index],
      }));
    },
    []
  );

  const [hidePromptSection, setHidePromptSection] = useState<boolean>(false);

  const promptsSection = (
    <div className={cn('flex h-full w-full flex-col overflow-y-hidden')}>
      <PlaygroundSectionHeader
        title="Prompts"
        collapsed={hidePromptSection}
        setCollapsed={setHidePromptSection}
        tooltipDescription={`Messages that will be sent to the AI model. Any variables will be populated using values from the evaluator reference dataset before sending to the model.`}
        className={cn('shrink-0 pl-6')}
      />
      <div
        className={
          hidePromptSection
            ? 'hidden'
            : 'flex h-full grow flex-row overflow-x-auto'
        }
      >
        <div className="relative flex-1">
          <div
            className={cn(
              'flex h-full w-full flex-col !overflow-x-auto',
              prompts.length > 1 && 'min-w-[500px]'
            )}
          >
            <EvaluatorPromptPanel
              promptCollapseState={promptCollapseState[0]}
              setPromptCollapseState={toggleCollapseAllMessagesForPrompt}
            />
          </div>
        </div>
        <div className="flex-1">
          <EvaluatorPlaygroundFeedbackSchemaPanel />
        </div>
      </div>
    </div>
  );

  const layout = PLAYGROUND_LAYOUT_SIZES['vertical'];

  const renderBody = () => {
    return (
      <PlaygroundBody
        orientation="vertical"
        stackVertically={true}
        hidePromptSection={hidePromptSection}
        layout={layout}
        promptsSection={promptsSection}
        inputAndOutputSection={outputSection}
      />
    );
  };

  return (
    <div
      data-testid="playground-layout"
      className="h-full grow overflow-y-auto overflow-x-hidden"
      ref={scrollRef}
    >
      {renderBody()}
    </div>
  );
}
